import { Chip, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components/datagrid/Custom';
import { DataGridKey } from 'redux/slices/datagrid';
import { RootState, useSelector } from 'redux/store';
import useCustomDispatch from 'redux/dispatch';
import { useTranslator } from 'translation/useTranslator';
import { ITranslator } from 'translation/generated/translationInterface';
import { activeColumnDefinition } from 'components/datagrid/columns/ActiveColumnDefinition';
import { editColumnDefinition } from 'components/datagrid/columns/EditColumnDefinition';
import { getOrderTypes } from 'redux/slices/orderTypes';
import { OrderType } from '../../../../@types/orderTypes';
import OrderTypeDrawer from './OrderTypeDrawer';

type OrderTypeRow = OrderType & {
  metered: boolean;
  active: boolean;
  fieldCount: number;
};

type AdminOrderTypesProps = {
  displayAddDrawer: boolean;
  onCloseAddDrawer: () => void;
};

const AdminOrderTypes = ({ displayAddDrawer, onCloseAddDrawer }: AdminOrderTypesProps) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const { orderTypes, loading, fetched } = useSelector((state: RootState) => state.orderTypes);
  const [orderTypeToEdit, setOrderTypeToEdit] = useState<OrderType | null>(null);

  useEffect(() => {
    customDispatch({ action: getOrderTypes, disableSuccessMessage: true });
  }, [customDispatch]);

  const columns = (translator: ITranslator): GridColDef[] => [
    {
      field: 'id',
      headerName: 'id'
    },
    {
      field: 'key',
      headerName: translator.key(),
      flex: 1.1,
      renderCell: (params) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {params.row.key}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {params.row.id.slice(0, 8)}
          </Typography>
        </Stack>
      )
    },
    {
      field: 'name',
      headerName: translator.name(),
      flex: 1.3
    },
    {
      field: 'unitName',
      headerName: translator.unitName(),
      flex: 1
    },
    {
      field: 'metered',
      headerName: translator.metered(),
      type: 'boolean',
      width: 110,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.metered ? translator.metered() : translator.flat()}
          color={params.row.metered ? 'primary' : 'default'}
          variant="outlined"
        />
      )
    },
    {
      field: 'fieldCount',
      headerName: translator.customFields(),
      type: 'number',
      width: 130
    },
    {
      ...activeColumnDefinition(translator)
    },
    {
      ...editColumnDefinition((cellValues) => {
        setOrderTypeToEdit(cellValues.row as OrderType);
      })
    }
  ];

  const rows: OrderTypeRow[] = orderTypes.map((orderType) => ({
    ...orderType,
    metered: orderType.isMetered,
    active: orderType.isActive,
    fieldCount: orderType.customFields.length
  }));

  return (
    <>
      <CustomDataGrid
        dataGridKey={DataGridKey.OrderTypes}
        columns={columns(translator)}
        rows={rows}
        loading={loading.orderTypes === true || !fetched}
      />
      <OrderTypeDrawer
        open={displayAddDrawer || orderTypeToEdit !== null}
        orderType={orderTypeToEdit}
        onClose={() => {
          setOrderTypeToEdit(null);
          onCloseAddDrawer();
        }}
      />
    </>
  );
};

export default AdminOrderTypes;
